import { combineEpics, ofType } from 'redux-observable';
import { switchMap, catchError, mergeMap, tap, map } from 'rxjs/operators';
import LogRocket from 'logrocket';
import { normalize } from 'normalizr';
import api from 'api/index';
import { handleError } from 'base/errors/store/actions';
import { history } from 'index';
import * as actionTypes from './actionTypes';
import {
  loginSuccess,
  loginError,
  logoutSuccess,
  fetchMyUserSuccess,
  forgotPasswordSuccess,
  forgotPasswordError,
  resetPasswordSuccess,
  resetPasswordError,
  verifyResetTokenSuccess,
  verifyResetTokenError,
  init2FaSuccess,
  loginRequire2Fa,
  enable2FaSuccess,
  enable2FaError,
  logout,
  verifyResetAuthCodeSuccess,
  verifyResetAuthCodeError,
  ssoError,
  sso,
  changePasswordSuccess,
  changePasswordError,
} from './actions';
import { getLoginDestination } from './selectors';
import { entitiesFetchSuccess } from '../../../store/entities/actions';
import { userSchema } from '../../../store/schema';
import { closeAllModals, closeModal } from '../../../modal/store/actions';
import { isValidToken } from '../../util/jwt';

const loginEpic = (action$, state$) =>
  action$.pipe(
    ofType(actionTypes.LOGIN),
    switchMap(action =>
      api.auth.login(action.username, action.password, action.otp.replace(/\s/g, '')).pipe(
        mergeMap(response => {
          const { token } = response.body;
          localStorage.setItem('accessToken', token);
          LogRocket.identify(action.username, {
            name: action.username,
            email: action.username,
          });
          history.push(getLoginDestination(state$.value));
          return [loginSuccess(token)];
        }),
        catchError(error => {
          if (error.status && error.status === 403) {
            // By convention, a 403 error indicates that 2FA is required
            return [loginRequire2Fa()];
          }
          if (error.status && [400, 401, 404, 422].includes(error.status)) {
            return [loginError(error)];
          }
          return [handleError(error)];
        }),
      ),
    ),
  );

const ssoEpic = (action$, state$) =>
  action$.pipe(
    ofType(actionTypes.SSO),
    switchMap(action =>
      api.auth.sso().pipe(
        mergeMap(response => {
          const { token } = response.body;
          localStorage.setItem('accessToken', token);
          history.push(getLoginDestination(state$.value));
          return [loginSuccess(token)];
        }),
        catchError(error => {
          if (error.status) {
            // SSO attempt failed -> fail silently
            if (error.status === 403) {
              return [];
            }
            if (action.retryCount < 1) {
              return [sso(action.retryCount + 1)];
            }
            return [ssoError(error)];
          }
          return [handleError(error)];
        }),
      ),
    ),
  );

const logoutEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.LOGOUT),
    switchMap(action => {
      const nextActions = [closeAllModals(), logoutSuccess()];
      const jwt = localStorage.getItem('accessToken');
      if (jwt && isValidToken(jwt)) {
        // manual logout from backend
        localStorage.removeItem('accessToken');
        sessionStorage.removeItem('setup2FaToken');
        history.push(action.destination);
        return api.auth.logout().pipe(
          tap(() => {
            localStorage.removeItem('accessToken');
            sessionStorage.removeItem('setup2FaToken');
          }),
          map(() => nextActions),
          catchError(() => [closeAllModals(), logoutSuccess()]),
        );
      }
      // token expired
      localStorage.removeItem('accessToken');
      sessionStorage.removeItem('setup2FaToken');
      history.push(action.destination);
      return nextActions;
    }),
  );

const fetchMyUserEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.FETCH_MY_USER),
    switchMap(() =>
      api.auth.fetchMyUser().pipe(
        mergeMap(response => {
          const { entities, result } = normalize(response.body, userSchema);
          LogRocket.identify(response.body.email, {
            name: response.body.email,
            email: response.body.email,
          });
          return [entitiesFetchSuccess(entities), fetchMyUserSuccess(result)];
        }),
        catchError(error => [handleError(error)]),
      ),
    ),
  );

const forgotPasswordEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.FORGOT_PASSWORD),
    switchMap(action =>
      api.auth.forgotPassword(action.email).pipe(
        mergeMap(() => [forgotPasswordSuccess()]),
        catchError(error =>
          error.status && [400, 401, 404].includes(error.status) ? [forgotPasswordError(error)] : [handleError(error)],
        ),
      ),
    ),
  );

const changePasswordEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.CHANGE_PASSWORD),
    switchMap(action =>
      api.auth.changePassword(action.oldPassword, action.newPassword).pipe(
        mergeMap(() => [changePasswordSuccess(), logout()]),
        catchError(error =>
          error.status && [400, 401, 403, 422].includes(error.status)
            ? [changePasswordError(error)]
            : [handleError(error)],
        ),
      ),
    ),
  );

const resetPasswordEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.RESET_PASSWORD),
    switchMap(action =>
      api.auth.resetPassword(action.data).pipe(
        mergeMap(() => [resetPasswordSuccess()]),
        catchError(error =>
          error.status && [400, 401, 404].includes(error.status) ? [resetPasswordError(error)] : [handleError(error)],
        ),
      ),
    ),
  );

const verifyResetTokenEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.VERIFY_TOKEN),
    switchMap(action =>
      api.auth.verifyResetToken(action.token).pipe(
        mergeMap(() => [verifyResetTokenSuccess()]),
        catchError(error =>
          error.status && [400, 401, 404].includes(error.status)
            ? [verifyResetTokenError(error)]
            : [handleError(error)],
        ),
      ),
    ),
  );

const verifyResetAuthCodeEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.VERIFY_RESET_CODE),
    switchMap(action =>
      api.auth.verifyResetAuthCode(action.authCode, action.token).pipe(
        mergeMap(() => [verifyResetAuthCodeSuccess()]),
        catchError(error =>
          error.status && [400, 401, 404].includes(error.status)
            ? [verifyResetAuthCodeError(error)]
            : [handleError(error)],
        ),
      ),
    ),
  );

const init2FaEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.INIT_2FA),
    switchMap(() =>
      api.auth.init2Fa().pipe(
        mergeMap(response => [init2FaSuccess(response.body.qrCodeUrl, response.body.secret)]),
        catchError(error => [handleError(error)]),
      ),
    ),
  );

const enable2FaEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.ENABLE_2FA),
    switchMap(action =>
      api.auth.enable2Fa(action.otp.replace(/\s/g, ''), action.secret).pipe(
        map(response => normalize(response.body, userSchema)),
        mergeMap(({ entities, result }) => [
          entitiesFetchSuccess(entities),
          fetchMyUserSuccess(result),
          enable2FaSuccess(),
          closeModal('MY_SECURITY_SETTINGS'),
          logout('/login/2fa-setup-success'),
        ]),
        catchError(error =>
          error.status && [400, 403, 422].includes(error.status) ? [enable2FaError(error)] : [handleError(error)],
        ),
      ),
    ),
  );

export default combineEpics(
  loginEpic,
  logoutEpic,
  ssoEpic,
  fetchMyUserEpic,
  forgotPasswordEpic,
  resetPasswordEpic,
  changePasswordEpic,
  verifyResetTokenEpic,
  verifyResetAuthCodeEpic,
  init2FaEpic,
  enable2FaEpic,
);
